import React, { useEffect } from 'react';

const Compliance = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const styles = {
        container: {
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '120px 24px 60px',
            color: '#414141',
            fontFamily: 'Mulish, sans-serif',
        },
        header: {
            marginBottom: '40px',
            borderBottom: '1px solid #eee',
            paddingBottom: '20px',
        },
        h1: {
            fontSize: '36px',
            fontWeight: '800',
            color: '#2B2B2B',
            marginBottom: '10px',
        },
        lastUpdated: {
            color: '#6E6E6E',
            fontSize: '14px',
        },
        section: {
            marginBottom: '40px',
        },
        h2: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#D81F26', /* Kirusa Red */
            marginBottom: '16px',
            marginTop: '32px',
        },
        h3: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#3457D5', /* Corpusio Blue */
            marginBottom: '12px',
            marginTop: '24px',
        },
        p: {
            marginBottom: '16px',
            lineHeight: '1.7',
            fontSize: '16px',
        },
        ul: {
            marginBottom: '16px',
            paddingLeft: '24px',
        },
        li: {
            marginBottom: '8px',
            lineHeight: '1.7',
        },
        link: {
            color: '#3457D5',
            textDecoration: 'underline',
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.h1}>Compliance</h1>
                <div style={styles.lastUpdated}>Last Updated: January 31, 2026</div>
            </div>

            <div style={styles.section}>
                <p style={styles.p}>Kirusa Inc. is committed to responsible, lawful, and secure operation of Corpusio worldwide.</p>

                <h2 style={styles.h2}>Overview</h2>
                <p style={styles.p}>
                    This Compliance Statement describes the compliance principles and operational commitments for Corpusio — an AI-powered document intelligence and natural-language search service accessible via web, mobile, and messaging channels such as WhatsApp where offered (the “Services”). This statement is intended to help customers understand how we approach privacy, security, and regulatory compliance.
                </p>
                <p style={styles.p}>
                    This statement is informational only and does not create contractual obligations unless incorporated into a written agreement with Kirusa Inc.
                </p>

                <h2 style={styles.h2}>1. Data Protection Laws</h2>
                <p style={styles.p}>
                    We are committed to complying with applicable data protection and privacy laws, including (where applicable):
                </p>
                <ul style={styles.ul}>
                    <li style={styles.li}>GDPR (EU/EEA)</li>
                    <li style={styles.li}>DPDP Act, 2023 (India)</li>
                    <li style={styles.li}>CCPA/CPRA (California)</li>
                    <li style={styles.li}>Applicable African data protection laws (e.g., Senegal Law No. 2008-12; Ghana Data Protection Act, 2012; Burkina Faso Law No. 010-2004/AN; Côte d’Ivoire Law No. 2013-450), and other relevant local laws in Mali, Togo, Malawi, Zambia, Lesotho, Eswatini, and Benin.</li>
                </ul>
                <p style={styles.p}>
                    Customers are responsible for their own compliance obligations, including ensuring they have the rights and permissions to upload, connect, and process documents and personal data through the Services.
                </p>

                <h2 style={styles.h2}>2. Security Program</h2>
                <p style={styles.p}>
                    We use industry-standard technical and organizational measures designed to protect data, including:
                </p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Encryption in transit (TLS/HTTPS)</li>
                    <li style={styles.li}>Access controls and least-privilege practices</li>
                    <li style={styles.li}>Authentication and session security</li>
                    <li style={styles.li}>Logging and monitoring for security events</li>
                    <li style={styles.li}>Vulnerability management and patching practices</li>
                    <li style={styles.li}>Incident response procedures</li>
                    <li style={styles.li}>Lead routing audit trail: For demo/trial requests and follow-up messages submitted via the Corpusio GPT workflow, we may maintain an internal audit trail (including timestamps and sender-provided details) to support reliability, abuse prevention, and troubleshooting, with retention aligned to our Privacy Policy.</li>

                </ul>
                <p style={styles.p}>
                    No system is completely secure; we cannot guarantee absolute security.
                </p>

                <h2 style={styles.h2}>3. Access Controls and Customer Permissions</h2>
                <p style={styles.p}>Corpusio is designed to support organizational access controls, including:</p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Role-based access control (RBAC) for workspace users (where enabled)</li>
                    <li style={styles.li}>Administrative controls for managing users and permissions</li>
                    <li style={styles.li}>Enforcement of permissions for connected repositories as configured by the customer</li>
                </ul>
                <p style={styles.p}>
                    Customers are responsible for configuring access controls appropriately and managing user access.
                </p>

                <h2 style={styles.h2}>4. Data Processing and Customer Content</h2>
                <p style={styles.p}>
                    Corpusio processes customer-provided or customer-connected content (“Customer Content”) to provide indexing, retrieval, and contextual answering within the Services.
                </p>
                <p style={styles.p}>
                    We do not claim ownership of Customer Content.
                </p>

                <h2 style={styles.h2}>5. AI Features and Model Training</h2>
                <p style={styles.p}>
                    Corpusio may use AI techniques (including retrieval and generative features) to deliver the Services.
                </p>
                <p style={styles.p}>
                    We do not use Customer Content to train our models without explicit customer opt-in.
                </p>

                <h2 style={styles.h2}>6. Data Residency and Cross-Border Transfers</h2>
                <p style={styles.p}>
                    Corpusio may process data in the United States and other regions where Kirusa or its service providers operate.
                </p>
                <p style={styles.p}>
                    Where required by law, we apply appropriate safeguards for cross-border data transfers, such as contractual protections.
                </p>

                <h2 style={styles.h2}>7. Enterprise Requirements and Contractual Assurances</h2>
                <p style={styles.p}>
                    Enterprise customers may request additional compliance documentation or contractual protections, including (as applicable):
                </p>
                <ul style={styles.ul}>
                    <li style={styles.li}>Data Processing Addendum (DPA)</li>
                    <li style={styles.li}>Security and architecture overview</li>
                    <li style={styles.li}>Subprocessor information</li>
                    <li style={styles.li}>Penetration test summaries or security attestations (as available)</li>
                </ul>
                <p style={styles.p}>
                    Availability of these materials may depend on plan tier and a mutual NDA where appropriate.
                </p>

                <h2 style={styles.h2}>8. Messaging Channels (e.g., WhatsApp) and Third-Party Platforms</h2>
                <p style={styles.p}>
                    If messaging-channel access is enabled, processing may involve third-party platforms and their APIs. Customers should review and comply with the terms and policies of those platforms.
                </p>
                <p style={styles.p}>
                    Where a Corpusio-branded GPT experience is offered through a third-party AI platform (such as ChatGPT), that platform may process user prompts and messages under its own terms and privacy policy. Kirusa’s responsibilities and processing are limited to data received in Kirusa-operated systems (for example, lead routing endpoints where configured).
                </p>

                <h2 style={styles.h2}>9. Reporting and Responsible Use</h2>
                <p style={styles.p}>
                    Customers and users must not use the Services for illegal purposes, and must not upload or process content that violates applicable law or third-party rights.
                </p>
                <p style={styles.p}>
                    We may suspend or terminate accounts for misuse, policy violations, or legal compliance requirements.
                </p>

                <h2 style={styles.h2}>10. Contact</h2>
                <p style={styles.p}>
                    For compliance inquiries, documentation requests (e.g., DPA), or security questions, contact:
                </p>
                <p style={styles.p}>
                    <strong>Email:</strong> <a href="mailto:support-corpusio@kirusa.com" style={styles.link}>support-corpusio@kirusa.com</a>
                </p>
                <p style={styles.p}>
                    <strong>Address:</strong> Kirusa Inc., 85 Swenson Circle, Berkeley Heights, NJ 07922, USA
                </p>

                <div style={{ marginTop: '60px', borderTop: '1px solid #eee', paddingTop: '20px', fontSize: '14px', color: '#6E6E6E' }}>
                    &copy; 2026 Kirusa Inc. All rights reserved.
                </div>
            </div>
        </div>
    );
};

export default Compliance;
